/**
 * Kiosk Configuration API
 * Handles API calls for loading and saving kiosk UI customization
 *
 * CRITICAL: Uses MERGE strategy to preserve existing operator settings
 */

import { BASE_KIOSK_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import {
  OperatorData,
  SaveKioskConfigRequest,
  SaveKioskConfigResponse,
  GetKioskConfigResponse,
} from '../types/kiosk-api-types';
import { KioskUICustomization } from '../types/kiosk-types';
import {
  extractUiCustomization,
  mergeUiCustomizationIntoSettings,
} from '../../core/utils/config-serializer';

/**
 * Kiosk Configuration API
 * Integrates with Kiosk API for operator settings management
 */
export class KioskConfigApi {
  constructor(
    private readonly baseUrl: string = BASE_KIOSK_API,
    private readonly tokenManager: TokenManager
  ) {}

  /**
   * Get authentication headers
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const headers = await this.tokenManager.getAuthHeaders();
    return {
      'Content-Type': 'application/json',
      ...headers,
    };
  }

  /**
   * Get operator data including settings
   * API: GET /kiosk/v1/admin/operator?operatorId={id}
   *
   * @param operatorId - Operator ID
   * @returns Operator data with settings
   */
  async getOperator(operatorId: number): Promise<OperatorData> {
    const headers = await this.getAuthHeaders();
    const url = `${this.baseUrl}/admin/operator?operatorId=${operatorId}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Kiosk Config API',
      `/admin/operator?operatorId=${operatorId}`
    );

    const data = await response.json();
    return data as OperatorData;
  }

  /**
   * Get operator UI customization settings
   * Extracts uiCustomization from operator settings
   *
   * @param operatorId - Operator ID
   * @returns Operator data with parsed UI customization
   */
  async getOperatorSettings(operatorId: number): Promise<GetKioskConfigResponse> {
    const operator = await this.getOperator(operatorId);

    // Extract UI customization from settings string
    let uiCustomization: KioskUICustomization | null = null;

    if (operator.settings) {
      uiCustomization = extractUiCustomization<typeof import('../types/kiosk-types').KioskIcons>(
        operator.settings
      );
    }

    return {
      operator,
      uiCustomization,
    };
  }

  /**
   * Save operator settings with UI customization
   * CRITICAL: Merges with existing settings to preserve all fields
   *
   * API: POST /kiosk/v1/admin/addOperator
   *
   * @param operatorId - Operator ID
   * @param uiCustomization - UI customization config to save
   * @param notify - Whether to notify users of changes (default: false)
   * @returns Save result
   */
  async saveOperatorSettings(
    operatorId: number,
    uiCustomization: KioskUICustomization,
    notify = false
  ): Promise<SaveKioskConfigResponse> {
    try {
      // Step 1: GET existing operator data
      const existingOperator = await this.getOperator(operatorId);

      // Step 2: Merge UI customization into settings
      // This PRESERVES existing settings like language, licensePlate, filter, etc.
      const mergedSettings = mergeUiCustomizationIntoSettings(
        existingOperator.settings || '{}',
        uiCustomization
      );

      // Step 3: Build save request
      const request: SaveKioskConfigRequest = {
        id: operatorId,
        countryCode: existingOperator.countryCode,
        partyId: existingOperator.partyId,
        settings: mergedSettings, // Merged settings with all fields preserved
        notify,
      };

      // Step 4: POST to /admin/addOperator
      const headers = await this.getAuthHeaders();
      const url = `${this.baseUrl}/admin/addOperator${notify ? '?notify=true' : ''}`;

      const response = await ApiErrorMiddleware.fetchWithErrorHandling(
        url,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(request),
        },
        'Kiosk Config API',
        '/admin/addOperator'
      );

      // API returns 200 with no content on success
      if (response.ok) {
        return {
          success: true,
          operator: {
            ...existingOperator,
            settings: mergedSettings,
          },
        };
      }

      // Handle unexpected response
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.error || 'Failed to save kiosk configuration',
      };
    } catch (error) {
      console.error('Failed to save kiosk configuration:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }
}
